import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { UrlDisplayHelper } from '../../../../../../../utils/url-display-helper';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { MobileFrameComponent } from '../../../preview-common/mobile-frame/mobile-frame.component';

/**
 * Skippable in-stream ad on a Google video partner site — the placements that
 * run the same video creative outside YouTube. The publisher page is a generic
 * article list; the ad takes over the top of it as a dismissable panel with the
 * player in the middle. The 16:9 placement.
 */
@Component({
    selector: 'app-gen-video-partners-in-stream-mobile',
    imports: [CommonModule, MobileFrameComponent],
    templateUrl: './video-partners-in-stream-mobile.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './video-partners-in-stream-mobile.component.scss'
})
export class VideoPartnersInStreamMobilePreviewComponent {
  @Input() data!: GenVideoPreviewData;

  /** The creative format this placement previews, found by measured ratio. */
  readonly format: VideoFormat = '16:9';

  /** Skeleton article rows under the ad; the last one clips at the frame. */
  readonly articles = [1, 2, 3, 4];

  /** Thumbnail of the 16:9 video, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailForFormat(this.videoIds, this.format);
  }

  /**
   * True while any link is still being measured, so the empty player says so
   * rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolvingAny(this.videoIds);
  }

  /** The host, which is all the panel shows of the destination. */
  get displayUrl(): string {
    return UrlDisplayHelper.hostOf(this.data.url);
  }

  /**
   * One bold line above the player, so it takes the short headline and only
   * falls back to the long one when there isn't one.
   */
  get headline(): string {
    return this.data.headlines[0] || this.data.longHeadlines[0] || 'Your headline appears here';
  }

  private get videoIds(): string[] {
    return this.data.videos.map((video) => video.videoId);
  }
}
